import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Banknote, 
  FileText, 
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Calendar,
  Users,
  Building,
  Lock,
  X,
  GitBranch,
  TrendingUp,
  TrendingDown,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';
import purchaseRequestService from '@/services/purchaseRequestService';
import financeService from '@/services/financeService';
import PurchaseReceipt from '@/components/PurchaseRequest/PurchaseReceipt';
import APARSummaryCards from './APARSummaryCards';
import SubsidiaryBreakdownModal from './SubsidiaryBreakdownModal';
import EmployeesOnLeaveModal from '@/modules/dashboard/components/EmployeesOnLeaveModal';
import leaveService from '@/services/leaveService';
import useNotificationRefresh from '@/hooks/useNotificationRefresh';
import AuditLogs from '@/modules/admin/AuditLogs';

const SUBSIDIARIES = [
  { value: "puffin_group", label: "Puffin Group Ltd" },
  { value: "buildwise", label: "Buildwise Construction Ltd" },
  { value: "laser_shield", label: "Laser Shield Ltd." },
  { value: "maz_logistics", label: "Maz Logistics Ltd." },
  { value: "puffin_energy", label: "Puffin Energy Ltd." },
  { value: "swingtide", label: "Swingtide Consulting Ltd" },
  { value: "puffin_agro", label: "Puffin Agro Allied" },
];

const GMDDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [rejectedHistory, setRejectedHistory] = useState([]); // GMD Rejected requests
  const [financeApprovedHistory, setFinanceApprovedHistory] = useState([]);
  const [approvedTransactions, setApprovedTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('gmd');
  const [loading, setLoading] = useState(true);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showSubsidiaryBreakdown, setShowSubsidiaryBreakdown] = useState(false);
  const [subsidiaryStats, setSubsidiaryStats] = useState([]);
  const [showPendingRequestsModal, setShowPendingRequestsModal] = useState(false);
  const [showPendingTransactionsModal, setShowPendingTransactionsModal] = useState(false);
  const [showApprovedRequestsModal, setShowApprovedRequestsModal] = useState(false);
  const [showApprovedTransactionsModal, setShowApprovedTransactionsModal] = useState(false);
  const [showFinanceApprovedRequestsModal, setShowFinanceApprovedRequestsModal] = useState(false);
  const [showAPARBreakdownModal, setShowAPARBreakdownModal] = useState(false);
  const [receivablesSummary, setReceivablesSummary] = useState(null);
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [showOnLeaveModal, setShowOnLeaveModal] = useState(false);
  const [onLeaveData, setOnLeaveData] = useState({ count: 0, leaves: [] });
  const [selectedSubsidiary, setSelectedSubsidiary] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of year
    to: new Date().toISOString().split('T')[0] // Today
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    // Reset selected subsidiary when modal closes
    if (!showExpensesModal) {
      setSelectedSubsidiary(null);
    }
  }, [showExpensesModal]);

  useEffect(() => {
    // Update PWA badge count for pending items
    const updateBadgeCount = async () => {
      const totalPending = pendingApprovals.length + pendingTransactions.length;
      
      if ('setAppBadge' in navigator) {
        try {
          if (totalPending > 0) {
            await navigator.setAppBadge(totalPending);
            console.log(`PWA Badge updated: ${totalPending} pending items`);
          } else {
            await navigator.clearAppBadge();
            console.log('PWA Badge cleared: No pending items');
          }
        } catch (error) {
          console.log('Badge API not supported or failed:', error);
        }
      }
      
      // Also update document title for browser tab
      if (totalPending > 0) {
        document.title = `(${totalPending}) GMD Dashboard`;
      } else {
        document.title = 'GMD Dashboard';
      }
    };

    updateBadgeCount();

    // Cleanup: Clear badge when component unmounts
    return () => {
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
      document.title = 'GMD Dashboard';
    };
  }, [pendingApprovals.length, pendingTransactions.length]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, approvalsRes, historyRes] = await Promise.all([
        purchaseRequestService.getDashboardStats(),
        purchaseRequestService.getAllRequests({ 
          status: 'pending_md_approval',
          current_stage: 'md_approval_stage',
          show_all: true,
          limit: 10
        }),
        purchaseRequestService.getAllRequests({
          show_all: true,
          limit: 100
        })
      ]);

      if (statsRes.data.success) {
        const statsData = statsRes.data.data;
        setStats({
          total_requests: statsData.overview?.total_requests || 0,
          completed_requests: statsData.overview?.completed_requests || 0,
          total_amount: statsData.overview?.total_amount || 0
        });
      }
      
      if (approvalsRes.data.success) {
        const requests = approvalsRes.data.data.requests || [];
        console.log('=== Pending Approvals ===');
        console.log('Pending approvals count:', requests.length);
        setPendingApprovals(requests);
      }
      
      // Fetch pending transactions separately to avoid breaking existing flow
      try {
        const transactionsRes = await financeService.getPendingGMDTransactions({ limit: 50 });
        console.log('=== Pending Transactions Response ===', transactionsRes.data);
        if (transactionsRes.data.success) {
          const pendingTxns = transactionsRes.data.data?.transactions || [];
          console.log('Pending transactions count:', pendingTxns.length);
          setPendingTransactions(pendingTxns);
        }
        
        // Fetch ALL transactions approved by GMD (no limit - shows all history)
        // These are transactions that passed GMD approval stage
        const approvedTransactionsRes = await financeService.getApprovedTransactions({});
        console.log('=== Approved Transactions Response ===', approvedTransactionsRes.data);
        if (approvedTransactionsRes.data.success) {
          const allApprovedTransactions = approvedTransactionsRes.data.data?.transactions || [];
          console.log('GMD Approved Transactions count:', allApprovedTransactions.length);
          setApprovedTransactions(allApprovedTransactions);
        }
      } catch (error) {
        console.log('Transactions API error:', error);
        setPendingTransactions([]);
        setApprovedTransactions([]);
      }
      
      // Fetch employees currently on leave for the dashboard count card + modal
      try {
        const onLeaveRes = await leaveService.getCurrentLeaves({ limit: 100 });
        // leaveService.getCurrentLeaves returns the unwrapped body, so the
        // leaves array lives at onLeaveRes.data.leaves.
        const onLeaveLeaves = onLeaveRes?.data?.leaves || [];
        setOnLeaveData({
          count: onLeaveRes?.data?.pagination?.totalItems || onLeaveLeaves.length,
          leaves: onLeaveLeaves
        });
      } catch (error) {
        console.log('Employees on leave API error:', error);
        setOnLeaveData({ count: 0, leaves: [] });
      }
      
      // Fetch receivables with payment history for accurate income tracking
      try {
        const receivablesRes = await financeService.getReceivables({});
        if (receivablesRes?.data?.success) {
          const receivablesData = receivablesRes.data.data.receivables || [];
          
          // Fetch payment history for each receivable
          const receivablesWithPayments = await Promise.all(
            receivablesData.map(async (receivable) => {
              try {
                const paymentsResponse = await financeService.getReceivablePaymentHistory(receivable.id);
                const payments = paymentsResponse.data.success ? paymentsResponse.data.data.payments || [] : [];
                return { ...receivable, Payments: payments };
              } catch (error) {
                return { ...receivable, Payments: [] };
              }
            })
          );
          
          // Calculate totals
          const totalAmount = receivablesWithPayments.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
          const receivedAmount = receivablesWithPayments.reduce((sum, r) => {
            if (r.Payments && Array.isArray(r.Payments)) {
              const paymentsTotal = r.Payments.reduce((paySum, payment) => paySum + parseFloat(payment.amount || 0), 0);
              return sum + paymentsTotal;
            }
            return sum;
          }, 0);
          const outstandingAmount = totalAmount - receivedAmount;
          
          setReceivablesSummary({
            receivables: {
              count: receivablesWithPayments.length,
              total_amount: totalAmount,
              received_amount: receivedAmount,
              outstanding_amount: outstandingAmount
            }
          });
        }
      } catch (error) {
        console.log('Receivables API error:', error);
        setReceivablesSummary(null);
      }
      
      if (historyRes.data.success) {
        const allRequests = historyRes.data.data.requests || [];
        
        console.log('=== DEBUG: Fetching Approval History ===');
        console.log('Total requests fetched:', allRequests.length);
        
        // GMD Approved: Requests that passed through MD approval stage
        // These are high-value requests (≥ ₦350,000) that have moved past md_approval_stage
        // Meaning GMD approved them and they're now in payment or later stages
        const gmdApproved = allRequests.filter(req => {
          const amount = parseFloat(req.amount) || 0;
          const isHighValue = amount >= 350000;
          
          // Check if request passed MD approval stage (moved to pay_vendor_stage or later)
          const passedMDApproval = req.current_stage && [
            'pay_vendor_stage',
            'confirm_payment_stage', 
            'delivery_stage',
            'completed'
          ].includes(req.current_stage);
          
          // Also check status to ensure it was approved (not rejected)
          const wasApproved = req.status && ![
            'rejected',
            'cancelled'
          ].includes(req.status);
          
          return isHighValue && passedMDApproval && wasApproved;
        }).sort((a, b) => {
          // Sort by GMD approval time (when they moved past MD stage)
          // Get the timestamp when GMD approved (finance stage approval that moved to MD stage)
          const getGMDApprovalTime = (req) => {
            const approvals = req.WorkflowApprovals || req.audit_trail || [];
            // Find when MD approved (moved from md_approval_stage to pay_vendor_stage)
            const mdApproval = approvals.find(approval => 
              approval.stage === 'md_approval_stage' && approval.action === 'approve'
            );
            return mdApproval?.created_at || req.updated_at || req.created_at;
          };
          
          const timeA = new Date(getGMDApprovalTime(a));
          const timeB = new Date(getGMDApprovalTime(b));
          return timeB - timeA; // Most recent first
        });
        
        console.log('GMD Approved count (passed MD approval):', gmdApproved.length);
        console.log('Sample GMD approved stages:', gmdApproved.slice(0, 3).map(r => ({ 
          id: r.request_id, 
          stage: r.current_stage, 
          status: r.status,
          amount: r.amount 
        })));
        
        // Finance Approved: Low-value requests (< ₦350,000) that don't need GMD approval
        // These go directly from finance_stage to payment stages
        const financeApproved = allRequests.filter(req => {
          const amount = parseFloat(req.amount) || 0;
          const isLowValue = amount < 350000;
          
          // Check if request is in payment or later stages (approved by finance)
          const passedFinanceApproval = req.current_stage && [
            'pay_vendor_stage',
            'confirm_payment_stage',
            'delivery_stage', 
            'completed'
          ].includes(req.current_stage);
          
          // Also check status
          const wasApproved = req.status && ![
            'rejected',
            'cancelled'
          ].includes(req.status);
          
          return isLowValue && passedFinanceApproval && wasApproved;
        });
        
        console.log('Finance Approved count (low-value approved):', financeApproved.length);
        
        // GMD Rejected: Requests rejected by GMD at MD approval stage
        const gmdRejected = allRequests.filter(req => {
          return req.status === 'rejected' && req.current_stage === 'md_approval_stage';
        }).sort((a, b) => {
          // Sort by rejection date (most recent first)
          const timeA = new Date(a.rejected_at || a.updated_at);
          const timeB = new Date(b.rejected_at || b.updated_at);
          return timeB - timeA;
        });
        
        console.log('GMD Rejected count:', gmdRejected.length);
        console.log('Sample GMD rejected:', gmdRejected.slice(0, 3).map(r => ({ 
          id: r.request_id, 
          rejected_by: r.rejected_by,
          rejection_reason: r.rejection_reason 
        })));
        
        setApprovalHistory(gmdApproved);
        setFinanceApprovedHistory(financeApproved);
        setRejectedHistory(gmdRejected);
        
        // Calculate subsidiary breakdown
        calculateSubsidiaryStats([...gmdApproved, ...financeApproved]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSubsidiaryStats = (allRequests) => {
    const subsidiaryMap = {};
    
    // Initialize all subsidiaries with zero values
    SUBSIDIARIES.forEach(sub => {
      subsidiaryMap[sub.value] = {
        name: sub.label,
        value: sub.value,
        totalAmount: 0,
        requestCount: 0,
        requests: []
      };
    });
    
    // Group requests by subsidiary
    allRequests.forEach(req => {
      let subsidiaryCode = null;
      let subsidiaryName = null;
      
      // Get subsidiary info from the relationship
      if (req.SubsidiaryInfo) {
        subsidiaryCode = req.SubsidiaryInfo.code;
        subsidiaryName = req.SubsidiaryInfo.name;
      }
      // Fallback to subsidiary string field (legacy)
      else if (req.subsidiary) {
        subsidiaryCode = req.subsidiary;
      }
      
      // If we found a subsidiary, add it to the map
      if (subsidiaryCode) {
        // Initialize if not in our predefined list
        if (!subsidiaryMap[subsidiaryCode]) {
          subsidiaryMap[subsidiaryCode] = {
            name: subsidiaryName || subsidiaryCode,
            value: subsidiaryCode,
            totalAmount: 0,
            requestCount: 0,
            requests: []
          };
        }
        
        subsidiaryMap[subsidiaryCode].totalAmount += parseFloat(req.amount) || 0;
        subsidiaryMap[subsidiaryCode].requestCount += 1;
        subsidiaryMap[subsidiaryCode].requests.push(req);
      }
    });
    
    // Convert to array, filter out empty subsidiaries, and sort by total amount
    const statsArray = Object.values(subsidiaryMap)
      .filter(sub => sub.requestCount > 0)
      .sort((a, b) => b.totalAmount - a.totalAmount);
    
    setSubsidiaryStats(statsArray);
  };

  const formatCurrency = (amount) => {
    return '₦' + new Intl.NumberFormat('en-NG', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getRelativeTime = (date) => {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now - past) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
  };

  const getStageTimestamp = (request) => {
    // Try to get the timestamp when request moved to MD approval stage
    if (request.md_stage_timestamp) {
      return request.md_stage_timestamp;
    }
    
    // Check workflow approvals for when finance approved (which moves to MD stage)
    const approvals = request.WorkflowApprovals || request.audit_trail || [];
    const financeApproval = approvals.find(approval => 
      approval.stage === 'finance_stage' && approval.action === 'approve'
    );
    
    if (financeApproval && financeApproval.created_at) {
      return financeApproval.created_at;
    }
    
    // Fallback to updated_at or created_at
    return request.updated_at || request.created_at;
  };

  // Auto-refresh when a GMD approval notification arrives via WebSocket
  useNotificationRefresh(['purchase_request_approval', 'workflow_popup'], fetchDashboardData);

  const handleApprove = async (requestId) => {
    setSelectedAction({ type: 'approve', requestId });
    setShowPinModal(true);
    setPin('');
    setPinError('');
  };

  const handleReject = async (requestId) => {
    setSelectedAction({ type: 'reject', requestId });
    setShowPinModal(true);
    setPin('');
    setPinError('');
  };

  const handleApproveTransaction = async (transactionId) => {
    setSelectedAction({ type: 'approve_transaction', transactionId });
    setShowPinModal(true);
    setPin('');
    setPinError('');
  };

  const handleRejectTransaction = async (transactionId) => {
    setSelectedAction({ type: 'reject_transaction', transactionId });
    setShowPinModal(true);
    setPin('');
    setPinError('');
  };

  const handlePinSubmit = async () => {
    if (!pin || pin.length !== 4) {
      setPinError('Please enter a 4-digit PIN');
      return;
    }

    try {
      const { type, requestId, transactionId } = selectedAction;
      
      if (type === 'approve') {
        await purchaseRequestService.processMDApproval(requestId, {
          action: 'approve',
          comments: 'Approved by GMD',
          pin
        });
      } else if (type === 'reject') {
        const reason = window.prompt('Enter rejection reason:');
        if (!reason) return;
        
        await purchaseRequestService.processMDApproval(requestId, {
          action: 'reject',
          comments: reason,
          pin
        });
      } else if (type === 'approve_transaction') {
        await financeService.approveGMDTransaction(transactionId, {
          pin,
          comments: 'Approved by GMD'
        });
      } else if (type === 'reject_transaction') {
        const reason = window.prompt('Enter rejection reason:');
        if (!reason) return;
        
        await financeService.rejectGMDTransaction(transactionId, {
          pin,
          comments: reason
        });
      }
      
      setShowPinModal(false);
      setPin('');
      setPinError('');
      fetchDashboardData();
    } catch (error) {
      console.error('Error processing approval:', error);
      setPinError(error.response?.data?.message || 'Invalid PIN or action failed');
    }
  };

  const [dashboardTab, setDashboardTab] = useState('overview');

  const dashboardTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'sales', label: 'Sales' },
    { id: 'order', label: 'Order' },
    { id: 'report', label: 'Report' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DF6B38]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Clean Header with Gradient Background */}
        <div className="rounded-2xl p-6 shadow-sm" style={{ 
          background: 'linear-gradient(to bottom, #16314C, #0d1f30)',
          border: '0.5px solid rgba(223, 107, 56, 0.3)'
        }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-white">GMD Dashboard</h1>
              <p className="text-sm text-gray-300 mt-1">Financial overview and approvals</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button 
                onClick={fetchDashboardData}
                className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all text-sm font-medium flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
              
              {pendingApprovals.length > 0 && (
                <div className="px-4 py-2 bg-[#DF6B38] rounded-xl shadow-sm">
                  <span className="text-sm font-semibold text-white">{pendingApprovals.length} Pending</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Financial Overview - Clean & Minimal */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Expenses */}
          <div 
            onClick={() => setShowExpensesModal(true)}
            className="bg-white rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Expenses</span>
              <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center">
                <TrendingDown className="h-4 w-4 text-red-600" strokeWidth={2} />
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-1">
              {formatCurrency(
                [...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
              )}
            </p>
            <p className="text-xs text-gray-400">
              {approvalHistory.length + financeApprovedHistory.length} requests
            </p>
          </div>

          {/* Total Income */}
          <div 
            onClick={() => navigate('/finance/receivables')}
            className="bg-white rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Income</span>
              <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-green-600" strokeWidth={2} />
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-1">
              {formatCurrency(receivablesSummary?.receivables?.received_amount || 0)}
            </p>
            <p className="text-xs text-gray-400">
              {receivablesSummary?.receivables?.count || 0} receivables
            </p>
          </div>

          {/* Net Position */}
          <div 
            onClick={() => setShowSubsidiaryBreakdown(true)}
            className="bg-white rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Net Position</span>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                (receivablesSummary?.receivables?.received_amount || 0) >= 
                (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                ? 'bg-green-50' : 'bg-orange-50'
              }`}>
                <BarChart3 className={`h-4 w-4 ${
                  (receivablesSummary?.receivables?.received_amount || 0) >= 
                  (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                  [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                  ? 'text-green-600' : 'text-orange-600'
                }`} strokeWidth={2} />
              </div>
            </div>
            <p className={`text-2xl font-semibold mb-1 ${
              (receivablesSummary?.receivables?.received_amount || 0) >= 
              (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
              ? 'text-green-600' : 'text-orange-600'
            }`}>
              {formatCurrency(
                (receivablesSummary?.receivables?.received_amount || 0) -
                (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
              )}
            </p>
            <p className="text-xs text-gray-400">
              {(receivablesSummary?.receivables?.received_amount || 0) >= 
              (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
              ? 'Healthy' : 'Warning'}
            </p>
          </div>
        </div>

        {/* Action Cards Grid - Minimal & Clean */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Pending Requests */}
          <div 
            onClick={() => setShowPendingRequestsModal(true)}
            className="bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-[#DF6B38]" strokeWidth={2} />
              </div>
              {pendingApprovals.length > 0 && (
                <span className="bg-[#DF6B38] text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">
                  {pendingApprovals.length}
                </span>
              )}
            </div>
            <p className="text-xl font-semibold text-gray-900 mb-0.5">{pendingApprovals.length}</p>
            <p className="text-xs text-gray-500">Pending Requests</p>
          </div>

          {/* Pending Transactions */}
          <div 
            onClick={() => setShowPendingTransactionsModal(true)}
            className="bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center">
                <Clock className="h-4 w-4 text-red-600" strokeWidth={2} />
              </div>
              {pendingTransactions.length > 0 && (
                <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">
                  {pendingTransactions.length}
                </span>
              )}
            </div>
            <p className="text-xl font-semibold text-gray-900 mb-0.5">{pendingTransactions.length}</p>
            <p className="text-xs text-gray-500">Pending Transactions</p>
          </div>

          {/* GMD Approved Requests */}
          <div 
            onClick={() => setShowApprovedRequestsModal(true)}
            className="bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-green-600" strokeWidth={2} />
              </div>
              <span className="text-[10px] text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded-lg">GMD</span>
            </div>
            <p className="text-xl font-semibold text-gray-900 mb-0.5">{approvalHistory.length}</p>
            <p className="text-xs text-gray-500">Requests Approved</p>
          </div>

          {/* GMD Approved Transactions */}
          <div 
            onClick={() => setShowApprovedTransactionsModal(true)}
            className="bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                <Banknote className="h-4 w-4 text-blue-600" strokeWidth={2} />
              </div>
              <span className="text-[10px] text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded-lg">GMD</span>
            </div>
            <p className="text-xl font-semibold text-gray-900 mb-0.5">{approvedTransactions.length}</p>
            <p className="text-xs text-gray-500">Transactions Approved</p>
          </div>

          {/* Finance Approved */}
          <div 
            onClick={() => setShowFinanceApprovedRequestsModal(true)}
            className="bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 bg-teal-50 rounded-xl flex items-center justify-center">
                <FileText className="h-4 w-4 text-teal-600" strokeWidth={2} />
              </div>
              <span className="text-[10px] text-teal-600 font-bold bg-teal-50 px-2 py-0.5 rounded-lg">Finance</span>
            </div>
            <p className="text-xl font-semibold text-gray-900 mb-0.5">{financeApprovedHistory.length}</p>
            <p className="text-xs text-gray-500">Requests Approved</p>
          </div>

          {/* Employees on Leave - opens modal with full list */}
          <div 
            onClick={() => setShowOnLeaveModal(true)}
            className="bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all shadow-sm"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 bg-yellow-50 rounded-xl flex items-center justify-center">
                <Calendar className="h-4 w-4 text-yellow-600" strokeWidth={2} />
              </div>
              {onLeaveData.count > 0 && (
                <span className="bg-yellow-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">
                  {onLeaveData.count}
                </span>
              )}
            </div>
            <p className="text-xl font-semibold text-gray-900 mb-0.5">{onLeaveData.count}</p>
            <p className="text-xs text-gray-500">Employees on Leave</p>
          </div>
        </div>

        {/* Enhanced Financial Chart with Date Range */}
        <div className="bg-white rounded-2xl p-6 mb-6" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[#DF6B38]" strokeWidth={1.5} />
                Financial Performance
              </h3>
              <p className="text-sm text-gray-500 mt-1">Income vs Expenses Analysis</p>
            </div>
            
            {/* Date Range Selector */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-[#DF6B38] focus:border-transparent"
                  style={{ borderColor: '#e5e7eb' }}
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-[#DF6B38] focus:border-transparent"
                  style={{ borderColor: '#e5e7eb' }}
                />
              </div>
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
                (receivablesSummary?.receivables?.received_amount || 0) >= 
                (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                ? 'bg-green-50 text-green-700' 
                : 'bg-orange-50 text-orange-700'
              }`}>
                {(receivablesSummary?.receivables?.received_amount || 0) >= 
                (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                ? '✓ Healthy' : '⚠ Warning'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart Section - ClearDesk Style */}
            <div className="lg:col-span-2 space-y-4">
              {/* Income Bar - ClearDesk Style */}
              <div className="bg-green-50 rounded-2xl p-5" style={{ border: '0.5px solid #d1fae5' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-800">Income</span>
                  </div>
                  <span className="text-2xl font-medium text-green-600">
                    {formatCurrency(receivablesSummary?.receivables?.received_amount || 0)}
                  </span>
                </div>
                <div className="relative h-10 bg-white rounded-xl overflow-hidden">
                  <div 
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400 rounded-xl transition-all duration-1000 ease-out flex items-center justify-end px-3"
                    style={{ 
                      width: `${Math.max(10, Math.min(100, ((receivablesSummary?.receivables?.received_amount || 0) / 
                        Math.max(
                          (receivablesSummary?.receivables?.received_amount || 1),
                          (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                          [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                        )) * 100))}%` 
                    }}
                  >
                    <span className="text-xs font-semibold text-white">
                      {Math.round(((receivablesSummary?.receivables?.received_amount || 0) / 
                        Math.max(
                          (receivablesSummary?.receivables?.received_amount || 1),
                          (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                          [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                        )) * 100)}%
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-3">{receivablesSummary?.receivables?.count || 0} receivables</p>
              </div>

              {/* Expenses Bar - ClearDesk Style */}
              <div className="bg-red-50 rounded-2xl p-5" style={{ border: '0.5px solid #fecaca' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-800">Expenses</span>
                  </div>
                  <span className="text-2xl font-medium text-red-600">
                    {formatCurrency(
                      [...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                      [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
                    )}
                  </span>
                </div>
                <div className="relative h-10 bg-white rounded-xl overflow-hidden">
                  <div 
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-500 to-red-400 rounded-xl transition-all duration-1000 ease-out flex items-center justify-end px-3"
                    style={{ 
                      width: `${Math.max(10, Math.min(100, ((([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                        [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))) / 
                        Math.max(
                          (receivablesSummary?.receivables?.received_amount || 1),
                          (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                          [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                        )) * 100))}%` 
                    }}
                  >
                    <span className="text-xs font-semibold text-white">
                      {Math.round(((([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                        [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))) / 
                        Math.max(
                          (receivablesSummary?.receivables?.received_amount || 1),
                          (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                          [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                        )) * 100)}%
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  {approvalHistory.length + financeApprovedHistory.length + pendingTransactions.length + approvedTransactions.length} items
                </p>
              </div>

              {/* Net Position - ClearDesk Style */}
              <div className={`rounded-2xl p-5 ${
                (receivablesSummary?.receivables?.received_amount || 0) >= 
                (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                ? 'bg-blue-50' 
                : 'bg-orange-50'
              }`} style={{ border: `0.5px solid ${
                (receivablesSummary?.receivables?.received_amount || 0) >= 
                (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                ? '#bfdbfe' : '#fed7aa'
              }` }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      (receivablesSummary?.receivables?.received_amount || 0) >= 
                      (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                      [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                      ? 'bg-blue-500' : 'bg-orange-500'
                    }`}></div>
                    <span className="text-sm font-semibold text-gray-800">Net Position</span>
                  </div>
                  <span className={`text-2xl font-medium ${
                    (receivablesSummary?.receivables?.received_amount || 0) >= 
                    (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                    [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                    ? 'text-blue-600' : 'text-orange-600'
                  }`}>
                    {formatCurrency(
                      (receivablesSummary?.receivables?.received_amount || 0) -
                      (([...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                      [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)))
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AP/AR Summary Cards */}
        <APARSummaryCards onViewDetails={(type) => setShowAPARBreakdownModal(true)} />

        {/* Pending MD Approvals */}
      <div className="rounded-2xl shadow-md p-8 relative overflow-hidden" style={{
        background: 'linear-gradient(to bottom, #16314C, #0d1f30)',
        border: '0.5px solid rgba(223, 107, 56, 0.3)'
      }}>
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full blur-3xl -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full blur-3xl -ml-24 -mb-24"></div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-[#DF6B38] rounded blur-md opacity-50"></div>
                <div className="relative bg-[#DF6B38] p-3 rounded-xl">
                  <AlertCircle className="h-6 w-6 text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-white">Procurement Requests</h3>
                <p className="text-sm text-gray-300">Requiring Your Approval</p>
              </div>
            </div>
            {pendingApprovals.length > 0 && (
              <div className="relative">
                <div className="absolute inset-0 bg-[#DF6B38] rounded-full blur-md opacity-30 animate-pulse"></div>
                <div className="relative px-5 py-2.5 bg-[#DF6B38] text-white rounded-full text-sm font-semibold shadow-lg">
                  {pendingApprovals.length} Pending
                </div>
              </div>
            )}
          </div>

          {pendingApprovals.length === 0 ? (
            <div className="text-center py-16 bg-white/50 backdrop-blur-sm rounded-2xl border-2 border-dashed border-gray-200">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-green-400 rounded-full blur-xl opacity-20"></div>
                <CheckCircle className="relative h-20 w-20 text-green-500 mx-auto mb-4" />
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-1">All Clear!</p>
              <p className="text-gray-500">No pending approvals at the moment</p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingApprovals.map((request, index) => (
                <div 
                  key={request.id} 
                  className="group relative bg-white rounded-2xl shadow-md border-2 border-gray-100 hover:border-orange-300 hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  style={{
                    animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`
                  }}
                >
                  {/* Hover Gradient Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-50/0 via-orange-50/50 to-orange-50/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="relative p-6">
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1 space-y-5">
                        {/* Request Header with Badges */}
                        <div className="flex items-center flex-wrap gap-2.5">
                          {/* Request ID Badge */}
                          <div className="relative group/badge">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded blur-sm opacity-50 group-hover/badge:opacity-75 transition-opacity"></div>
                            <div className="relative flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-2 rounded shadow-lg">
                              <FileText className="h-5 w-5 text-white" />
                              <span className="text-base font-bold text-white tracking-wide">{request.request_id}</span>
                            </div>
                          </div>

                          {/* High Value Badge */}
                          <div className="relative">
                            <div className="absolute inset-0 bg-orange-500 rounded-lg blur-sm opacity-30"></div>
                            <span className="relative px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md">
                              HIGH VALUE
                            </span>
                          </div>

                          {/* Subsidiary Badge */}
                          {(request.SubsidiaryInfo || request.subsidiary) && (
                            <div className="flex items-center space-x-1.5 bg-gradient-to-r from-purple-500 to-indigo-500 px-3 py-1.5 rounded-lg shadow-md">
                              <Building className="h-4 w-4 text-white" />
                              <span className="text-xs font-bold text-white">
                                {request.SubsidiaryInfo?.name || 
                                 SUBSIDIARIES.find(s => s.value === request.subsidiary)?.label || 
                                 request.subsidiary}
                              </span>
                            </div>
                          )}

                          {/* Time Badge */}
                          <div className="flex flex-col bg-gradient-to-br from-orange-50 to-red-50 px-3 py-1.5 rounded-lg border-2 border-orange-200 shadow-sm">
                            <div className="flex items-center text-xs font-bold text-orange-700">
                              <Clock className="h-3.5 w-3.5 mr-1.5" />
                              Arrived {getRelativeTime(getStageTimestamp(request))}
                            </div>
                            <div className="flex items-center text-xs text-gray-500 mt-0.5">
                              <Calendar className="h-3 w-3 mr-1" />
                              Created: {new Date(request.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Request Details Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {/* Requester Card */}
                          <div className="relative group/card bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 rounded p-4 border-2 border-blue-100 hover:border-blue-300 transition-all shadow-sm hover:shadow-md">
                            <div className="flex items-center text-xs text-blue-600 mb-2.5 font-bold uppercase tracking-wide">
                              <Users className="h-4 w-4 mr-1.5" />
                              Requester
                            </div>
                            <div className="font-bold text-gray-900 text-lg mb-1">{request.requester_name}</div>
                            <div className="flex items-center text-sm text-gray-600 bg-white/60 px-2 py-1 rounded-md">
                              <Building className="h-3.5 w-3.5 mr-1.5" />
                              {request.department}
                            </div>
                          </div>
                          
                          {/* Item/Service Card */}
                          <div className="relative group/card bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 rounded p-4 border-2 border-purple-100 hover:border-purple-300 transition-all shadow-sm hover:shadow-md">
                            <div className="flex items-center text-xs text-purple-600 mb-2.5 font-bold uppercase tracking-wide">
                              <FileText className="h-4 w-4 mr-1.5" />
                              Item/Service
                            </div>
                            <div className="font-bold text-gray-900 text-sm line-clamp-2 mb-2 leading-snug">
                              {request.item_service_requested}
                            </div>
                            <div className="text-xs text-gray-600 bg-white/60 px-2 py-1.5 rounded-md font-medium">
                              Vendor: {request.vendor_name}
                            </div>
                          </div>
                          
                          {/* Amount Card */}
                          <div className="relative overflow-hidden bg-gradient-to-br from-green-500 via-emerald-500 to-green-600 rounded p-4 border-2 border-green-400 text-white shadow-lg hover:shadow-xl transition-all">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                            <div className="relative">
                              <div className="flex items-center text-xs text-green-100 mb-2.5 font-bold uppercase tracking-wide">
                                <Banknote className="h-4 w-4 mr-1.5" />
                                Amount
                              </div>
                              <div className="text-3xl font-bold tracking-tight">
                                {formatCurrency(request.amount)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Workflow History */}
                        {request.WorkflowApprovals && request.WorkflowApprovals.length > 0 && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded p-4 border-2 border-green-100">
                            <div className="text-sm text-green-700 font-bold mb-3 flex items-center">
                              <GitBranch className="h-4 w-4 mr-2 text-green-600" />
                              Approval History
                            </div>
                            <div className="space-y-2">
                              {request.WorkflowApprovals.map((approval, index) => (
                                <div key={index} className="flex items-center space-x-2.5 text-sm bg-white px-3 py-2.5 rounded-lg border border-green-200 shadow-sm">
                                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <CheckCircle className="h-4 w-4 text-white" />
                                  </div>
                                  <span className="text-gray-700 font-medium">
                                    <span className="font-bold text-green-700">
                                      {approval.stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                    {' '}- {approval.action} by {approval.approver_name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col space-y-3 min-w-[140px]">
                        <button
                          onClick={() => handleApprove(request.request_id)}
                          className="group/btn relative px-5 py-3 text-white rounded-xl hover:opacity-90 transition-all text-sm font-semibold flex items-center justify-center shadow-md hover:shadow-lg"
                          style={{ background: 'linear-gradient(135deg, #DF6B38, #DF383F)' }}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(request.request_id)}
                          className="group/btn relative px-5 py-3 bg-white border-2 text-gray-700 rounded-xl hover:bg-gray-50 transition-all text-sm font-semibold flex items-center justify-center"
                          style={{ borderColor: '#e5e7eb' }}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Reject
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowReceipt(true);
                          }}
                          className="px-5 py-3 border-2 text-gray-700 rounded-xl hover:bg-gray-50 transition-all text-sm font-semibold"
                          style={{ borderColor: '#e5e7eb' }}
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowDocumentViewer(true);
                          }}
                          className="px-5 py-3 text-white rounded-xl hover:opacity-90 transition-all text-sm font-semibold flex items-center justify-center shadow-md"
                          style={{ background: 'linear-gradient(to bottom, #16314C, #0d1f30)' }}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Attachment
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pending Finance Transactions */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Banknote className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Finance Transactions Requiring Your Approval</h3>
          </div>
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
            {pendingTransactions.length} Pending
          </span>
        </div>

        {pendingTransactions.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-3" />
            <p className="text-gray-600">No pending transactions</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingTransactions.map((transaction) => (
              <div key={transaction.id} className="bg-white rounded-2xl shadow-md border-2 border-gray-200 p-6 hover:shadow-xl hover:border-red-300 transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Transaction Header */}
                    <div className="flex items-center flex-wrap gap-3 mb-4">
                      <div className="flex items-center space-x-2 bg-red-50 px-3 py-1.5 rounded-lg">
                        <Banknote className="h-5 w-5 text-red-600" />
                        <span className="text-lg font-bold text-red-600">{transaction.transaction_number}</span>
                      </div>
                      <span className="px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm bg-red-100 text-red-800">
                        Finance Transaction
                      </span>
                      <div className="flex flex-col bg-gradient-to-r from-red-50 to-orange-50 px-3 py-1.5 rounded-lg border border-red-200">
                        <div className="flex items-center text-sm font-bold text-red-700">
                          <Clock className="h-4 w-4 mr-1.5" />
                          Arrived {getRelativeTime(transaction.created_at)}
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-0.5">
                          <Calendar className="h-3 w-3 mr-1" />
                          Created: {new Date(transaction.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Transaction Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      {/* Type Card */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded p-4 border border-blue-100">
                        <div className="flex items-center text-sm text-blue-600 mb-2 font-semibold">
                          <FileText className="h-4 w-4 mr-1.5" />
                          Type
                        </div>
                        <div className="font-bold text-gray-900 mb-1 capitalize">{transaction.type}</div>
                        <div className="text-sm text-gray-600">
                          {transaction.subsidiary && (
                            <span className="bg-white px-2 py-1 rounded text-xs">
                              {transaction.subsidiary.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Description Card */}
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded p-4 border border-purple-100">
                        <div className="flex items-center text-sm text-purple-600 mb-2 font-semibold">
                          <FileText className="h-4 w-4 mr-1.5" />
                          Description
                        </div>
                        <div className="font-bold text-gray-900 line-clamp-2 mb-2">
                          {transaction.description}
                        </div>
                        {transaction.reference_number && (
                          <div className="text-sm text-gray-600 bg-white px-2 py-1 rounded">
                            Ref: {transaction.reference_number}
                          </div>
                        )}
                      </div>
                      
                      {/* Amount Card */}
                      <div className="bg-red-500 rounded p-4 border border-red-400 text-white shadow-lg">
                        <div className="flex items-center text-sm text-red-100 mb-2 font-semibold">
                          <Banknote className="h-4 w-4 mr-1.5" />
                          Amount
                        </div>
                        <div className="text-2xl font-bold">
                          {formatCurrency(transaction.amount)}
                        </div>
                      </div>
                    </div>

                    {/* Account Details */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                        <div className="text-sm text-green-600 font-semibold mb-1">Debit Account</div>
                        <div className="text-gray-700 font-medium">
                          {transaction.DebitAccount?.code} - {transaction.DebitAccount?.name}
                        </div>
                      </div>
                      <div className="bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                        <div className="text-sm text-blue-600 font-semibold mb-1">Credit Account</div>
                        <div className="text-gray-700 font-medium">
                          {transaction.CreditAccount?.code} - {transaction.CreditAccount?.name}
                        </div>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="mb-4">
                      {transaction.payment_method === 'cash' ? (
                        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3 rounded-lg border-2 border-green-400 shadow-lg">
                          <div className="flex items-center gap-3">
                            <Banknote className="h-8 w-8 text-white" />
                            <div>
                              <div className="text-white font-bold text-xl">CASH PAYMENT</div>
                              <div className="text-green-100 text-sm">No beneficiary account details required</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-blue-50 px-4 py-3 rounded-lg border border-blue-200">
                          <div className="text-sm text-blue-600 font-semibold mb-3 flex items-center">
                            <Banknote className="h-4 w-4 mr-1.5" />
                            Bank Transfer - Beneficiary Details
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Beneficiary Name:</span>
                              <span className="font-semibold text-gray-900">{transaction.beneficiary_name || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Bank:</span>
                              <span className="font-semibold text-gray-900">{transaction.beneficiary_bank || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Account Number:</span>
                              <span className="font-mono font-semibold text-gray-900">{transaction.beneficiary_account || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Created By */}
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 font-semibold mb-2 flex items-center">
                        <Users className="h-4 w-4 mr-1.5 text-gray-600" />
                        Created By
                      </div>
                      <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                        <span className="text-gray-700 font-medium">
                          Finance Department
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          on {new Date(transaction.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-2 ml-6">
                    <button
                      onClick={() => handleApproveTransaction(transaction.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectTransaction(transaction.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center justify-center"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">This Month</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.monthly_requests || 0}</p>
          <p className="text-sm text-gray-600 mt-1">Purchase Requests</p>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Avg. Processing Time</h3>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats?.avg_processing_days || 0} days</p>
          <p className="text-sm text-gray-600 mt-1">From submission to completion</p>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Approval Rate</h3>
            <PieChart className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {stats?.total_requests > 0 
              ? Math.round((stats?.completed_requests / stats?.total_requests) * 100) 
              : 0}%
          </p>
          <p className="text-sm text-gray-600 mt-1">Successfully completed</p>
        </div>
      </div>

      {/* Approval History - Clean & Simple */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Approval History</h3>
            <p className="text-sm text-gray-500 mt-1">Track all approved requests and transactions</p>
          </div>
          
          {/* Simple Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('gmd')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'gmd'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              GMD Approved Requests ({approvalHistory.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              GMD Rejected ({rejectedHistory.length})
            </button>
            <button
              onClick={() => setActiveTab('finance')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'finance'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Finance ({financeApprovedHistory.length})
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'transactions'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Transactions ({approvedTransactions.length})
            </button>
          </div>
        </div>
        
        {activeTab === 'gmd' ? (
          approvalHistory.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No requests approved yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {approvalHistory.map((request) => {
                // Find the MD approval timestamp from WorkflowAuditLogs
                const mdApproval = request.WorkflowAuditLogs?.find(
                  log => log.stage === 'md_approval_stage' && log.action === 'md_approved'
                );
                const approvalDate = mdApproval?.timestamp || request.updated_at;
                
                return (
              <div 
                key={request.id} 
                className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <span className="px-3 py-1 bg-gray-900 text-white rounded-lg text-sm font-medium">
                      {request.request_id}
                    </span>
                    
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {request.item_service_requested}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {request.requester_name} • {formatCurrency(request.amount)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        GMD Approved: {new Date(approvalDate).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRequest(request);
                        setShowDocumentViewer(true);
                      }}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailsModal(true);
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            )})}
          </div>
        )
        ) : activeTab === 'rejected' ? (
          rejectedHistory.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No requests rejected by GMD</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rejectedHistory.map((request) => {
                // Find the MD rejection timestamp from WorkflowAuditLogs
                const mdRejection = request.WorkflowAuditLogs?.find(
                  log => log.stage === 'md_approval_stage' && log.action === 'md_rejected'
                );
                const rejectionDate = mdRejection?.timestamp || request.rejected_at || request.updated_at;
                
                return (
              <div 
                key={request.id} 
                className="bg-red-50 rounded-lg p-4 hover:bg-red-100 transition-colors border border-red-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <span className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm font-medium">
                      {request.request_id}
                    </span>
                    
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {request.item_service_requested}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {request.requester_name} • {formatCurrency(request.amount)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Rejected by: {request.rejected_by || 'GMD'} • {new Date(rejectionDate).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {request.rejection_reason && (
                        <p className="text-xs text-red-700 mt-2 bg-red-100 px-2 py-1 rounded">
                          <strong>Reason:</strong> {request.rejection_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRequest(request);
                        setShowDocumentViewer(true);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailsModal(true);
                      }}
                      className="px-4 py-2 border border-red-300 text-gray-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                    >
                      Details
                    </button>
                  </div>
                </div>
              </div>
            )})}
          </div>
        )
        ) : activeTab === 'finance' ? (
          financeApprovedHistory.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No finance-approved requests below threshold</p>
            </div>
          ) : (
            <div className="space-y-3">
              {financeApprovedHistory.map((request) => {
                // Find the finance approval timestamp from WorkflowAuditLogs
                const financeApproval = request.WorkflowAuditLogs?.find(
                  log => log.stage === 'finance_stage' && log.action === 'approve'
                );
                const approvalDate = financeApproval?.timestamp || request.updated_at;
                
                return (
                <div 
                  key={request.id} 
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <span className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm font-medium">
                        {request.request_id}
                      </span>
                      
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">
                          {request.item_service_requested}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {request.requester_name} • {formatCurrency(request.amount)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Finance Approved: {new Date(approvalDate).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRequest(request);
                          setShowDocumentViewer(true);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        View
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailsModal(true);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )
        ) : (
          approvedTransactions.length === 0 ? (
            <div className="text-center py-12">
              <Banknote className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No transactions approved by GMD yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {approvedTransactions.map((transaction) => (
                <div 
                  key={transaction.id} 
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <span className="px-3 py-1 bg-[#16314C] text-white rounded-lg text-sm font-medium">
                        TXN-{transaction.id}
                      </span>
                      
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">
                          {transaction.description}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {formatCurrency(transaction.amount)} • {transaction.payment_method === 'cash' ? 'Cash' : 'Transfer'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          GMD Approved: {new Date(transaction.approved_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1.5 bg-gray-700 text-white rounded text-sm font-medium">
                        Transaction
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Audit Logs - everything done across the system */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <AuditLogs embedded />
      </div>

      {/* PIN Verification Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Lock className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-xl font-semibold text-gray-900">Enter PIN to Confirm</h3>
              </div>
              <button
                onClick={() => {
                  setShowPinModal(false);
                  setPin('');
                  setPinError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Enter your 4-digit PIN to {selectedAction?.type} this purchase request.
              </p>
              
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  style={{ WebkitTextSecurity: 'disc' }}
                  value={pin}
                  onChange={(e) => {
                    setPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                    setPinError('');
                  }}
                  maxLength={4}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                  placeholder="••••"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && pin.length === 4) {
                      handlePinSubmit();
                    }
                  }}
                />
              </div>

              {pinError && (
                <p className="text-sm text-red-600 mt-2">{pinError}</p>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowPinModal(false);
                  setPin('');
                  setPinError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePinSubmit}
                disabled={pin.length !== 4}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceipt && selectedRequest && (
        <PurchaseReceipt 
          request={selectedRequest}
          onClose={() => {
            setShowReceipt(false);
            setSelectedRequest(null);
          }}
        />
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Request Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Request ID</label>
                  <p className="text-lg font-semibold text-gray-900">{selectedRequest.request_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Amount</label>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(selectedRequest.amount)}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Item/Service Requested</label>
                <p className="text-gray-900 mt-1">{selectedRequest.item_service_requested}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Requester</label>
                  <p className="text-gray-900">{selectedRequest.requester_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Department</label>
                  <p className="text-gray-900">{selectedRequest.department}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Vendor</label>
                  <p className="text-gray-900">{selectedRequest.vendor_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Method</label>
                  <p className="text-gray-900 capitalize">
                    {selectedRequest.payment_method === 'cash' ? 'Cash Payment' : 'Bank Transfer'}
                  </p>
                </div>
              </div>
              
              {selectedRequest.payment_method === 'transfer' && selectedRequest.vendor_bank_details && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Bank Details</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg text-sm">{selectedRequest.vendor_bank_details}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="text-gray-900 capitalize">{selectedRequest.status?.replace('_', ' ')}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Current Stage</label>
                <p className="text-gray-900 capitalize">{selectedRequest.current_stage?.replace('_', ' ')}</p>
              </div>
              
              {selectedRequest.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedRequest.notes}</p>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                {(selectedRequest.cdn_url || selectedRequest.supporting_document_path) && (
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setShowDocumentViewer(true);
                    }}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    View Document
                  </button>
                )}
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {showDocumentViewer && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded shadow-2xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Supporting Document</h3>
              <button
                onClick={() => setShowDocumentViewer(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                  selectedRequest.cdn_url || 
                  selectedRequest.supporting_document_path || 
                  selectedRequest.attachment_url ||
                  selectedRequest.document_url
                )}&embedded=true`}
                className="w-full h-full"
                title="Supporting Document"
              />
            </div>
          </div>
        </div>
      )}

      {/* Subsidiary Breakdown Modal */}
      {showSubsidiaryBreakdown && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gray-800 p-6 text-white border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Subsidiary Spending Breakdown</h3>
                  <p className="text-gray-300 mt-1">
                    Total across all subsidiaries: {formatCurrency(
                      subsidiaryStats.reduce((sum, sub) => sum + sub.totalAmount, 0)
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setShowSubsidiaryBreakdown(false)}
                  className="text-white hover:bg-gray-700 rounded p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {subsidiaryStats.length === 0 ? (
                <div className="text-center py-12">
                  <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No subsidiary data available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {subsidiaryStats.map((sub, index) => {
                    const totalAmount = subsidiaryStats.reduce((sum, s) => sum + s.totalAmount, 0);
                    const percentage = totalAmount > 0 ? (sub.totalAmount / totalAmount * 100) : 0;
                    
                    return (
                      <div 
                        key={sub.value}
                        className="bg-white rounded border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all"
                      >
                        {/* Subsidiary Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-12 w-12 rounded bg-gray-700 flex items-center justify-center text-white font-bold text-lg">
                              {sub.name.charAt(0)}
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-gray-900">{sub.name}</h4>
                              <p className="text-sm text-gray-600">{sub.requestCount} {sub.requestCount === 1 ? 'request' : 'requests'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">{formatCurrency(sub.totalAmount)}</p>
                            <p className="text-sm text-gray-500">{percentage.toFixed(1)}% of total</p>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-4">
                          <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                            <div 
                              className="h-full bg-gray-700 rounded transition-all"
                              style={{ 
                                width: `${percentage}%`
                              }}
                            />
                          </div>
                        </div>

                        {/* Request List */}
                        <div className="space-y-2">
                          <button
                            onClick={() => {
                              const element = document.getElementById(`sub-requests-${sub.value}`);
                              element.classList.toggle('hidden');
                            }}
                            className="text-sm text-gray-700 hover:text-gray-900 font-medium flex items-center space-x-1"
                          >
                            <span>View {sub.requestCount} {sub.requestCount === 1 ? 'request' : 'requests'}</span>
                            <span className="text-xs">▼</span>
                          </button>
                          
                          <div id={`sub-requests-${sub.value}`} className="hidden space-y-2 mt-3">
                            {sub.requests.map((req) => (
                              <div 
                                key={req.id}
                                className="bg-white border border-gray-200 rounded p-3 hover:border-gray-300 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="text-sm font-bold text-blue-600">{req.request_id}</span>
                                      <span className="text-xs text-gray-500">•</span>
                                      <span className="text-sm text-gray-700">{req.requester_name}</span>
                                    </div>
                                    <p className="text-sm text-gray-600 line-clamp-1">{req.item_service_requested}</p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <p className="text-sm font-bold text-green-600">{formatCurrency(req.amount)}</p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowSubsidiaryBreakdown(false)}
                className="w-full bg-gray-700 text-white py-3 rounded hover:bg-gray-800 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Requests Modal */}
      {showPendingRequestsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gray-800 p-6 text-white border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Pending Requests Requiring Your Approval</h3>
                  <p className="text-gray-300 mt-1">{pendingApprovals.length} requests awaiting GMD approval</p>
                </div>
                <button
                  onClick={() => setShowPendingRequestsModal(false)}
                  className="text-white hover:bg-gray-700 rounded p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500">No pending requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingApprovals.map((req, index) => (
                    <div key={req.id} className="bg-white rounded border border-gray-200 p-6 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-12 w-12 rounded bg-gray-700 flex items-center justify-center text-white font-bold">
                            {req.requester_name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-lg font-bold text-gray-900">{req.request_id}</h4>
                              {(req.SubsidiaryInfo || req.subsidiary) && (
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                                  <Building className="h-3 w-3" />
                                  {req.SubsidiaryInfo?.name || 
                                   SUBSIDIARIES.find(s => s.value === req.subsidiary)?.label || 
                                   req.subsidiary}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{req.requester_name} • {req.department}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                                Arrived {getRelativeTime(getStageTimestamp(req))}
                              </span>
                              <span className="text-xs text-gray-500">
                                Created: {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-orange-600">{formatCurrency(req.amount)}</p>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4">{req.item_service_requested}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowPendingRequestsModal(false);
                            handleApprove(req.request_id);
                          }}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setShowPendingRequestsModal(false);
                            handleReject(req.request_id);
                          }}
                          className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRequest(req);
                            setShowDetailsModal(true);
                            setShowPendingRequestsModal(false);
                          }}
                          className="px-4 bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowPendingRequestsModal(false)}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-700 text-white py-3 rounded-lg hover:from-orange-700 hover:to-orange-800 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Transactions Modal */}
      {showPendingTransactionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Pending Transactions Requiring Your Approval</h3>
                  <p className="text-red-100 mt-1">{pendingTransactions.length} transactions awaiting GMD approval</p>
                </div>
                <button
                  onClick={() => setShowPendingTransactionsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {pendingTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500">No pending transactions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingTransactions.map((txn) => (
                    <div key={txn.id} className="bg-gradient-to-br from-white to-red-50 rounded border-2 border-red-200 p-6 hover:shadow-lg transition-all">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-12 w-12 rounded-full bg-red-500 flex items-center justify-center text-white">
                            <Banknote className="h-6 w-6" />
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-gray-900">{txn.transaction_number}</h4>
                            <p className="text-sm text-gray-600 capitalize">{txn.type}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">
                                Arrived {getRelativeTime(txn.created_at)}
                              </span>
                              <span className="text-xs text-gray-500">
                                Created: {new Date(txn.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-red-600">{formatCurrency(txn.amount)}</p>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4">{txn.description}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowPendingTransactionsModal(false);
                            handleApproveTransaction(txn.id);
                          }}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setShowPendingTransactionsModal(false);
                            handleRejectTransaction(txn.id);
                          }}
                          className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowPendingTransactionsModal(false)}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-lg hover:from-red-700 hover:to-red-800 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approved Requests Modal */}
      {showApprovedRequestsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Requests Approved by GMD</h3>
                  <p className="text-green-100 mt-1">
                    {approvalHistory.length} requests • Total: {formatCurrency(
                      approvalHistory.reduce((sum, req) => sum + (Number(req.amount) || 0), 0)
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setShowApprovedRequestsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {approvalHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No approved requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvalHistory.map((req, index) => (
                    <div key={req.id} className="bg-gradient-to-br from-white to-green-50 rounded border-2 border-green-200 p-4 hover:shadow-lg transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                            {req.requester_name?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-blue-600">{req.request_id}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-sm text-gray-700">{req.requester_name}</span>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-1">{req.item_service_requested}</p>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-green-600">{formatCurrency(req.amount)}</p>
                          <p className="text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowApprovedRequestsModal(false)}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg hover:from-green-700 hover:to-green-800 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approved Transactions Modal */}
      {showApprovedTransactionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Transactions Approved by GMD</h3>
                  <p className="text-blue-100 mt-1">
                    {approvedTransactions.length} transactions • Total: {formatCurrency(
                      approvedTransactions.reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setShowApprovedTransactionsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {approvedTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <Banknote className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No approved transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvedTransactions.map((txn) => (
                    <div key={txn.id} className="bg-gradient-to-br from-white to-blue-50 rounded border-2 border-blue-200 p-4 hover:shadow-lg transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                            <Banknote className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-blue-600">#{txn.id}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-sm text-gray-700 capitalize">{txn.type}</span>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-1">{txn.description}</p>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-blue-600">{formatCurrency(txn.amount)}</p>
                          <p className="text-xs text-gray-500">{new Date(txn.approved_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowApprovedTransactionsModal(false)}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finance Approved Requests Modal */}
      {showFinanceApprovedRequestsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Requests Approved by Finance</h3>
                  <p className="text-teal-100 mt-1">
                    {financeApprovedHistory.length} requests • Total: {formatCurrency(
                      financeApprovedHistory.reduce((sum, req) => sum + (Number(req.amount) || 0), 0)
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setShowFinanceApprovedRequestsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {financeApprovedHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No finance-approved requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {financeApprovedHistory.map((req, index) => (
                    <div key={req.id} className="bg-gradient-to-br from-white to-teal-50 rounded border-2 border-teal-200 p-4 hover:shadow-lg transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="h-10 w-10 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold">
                            {req.requester_name?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-teal-600">{req.request_id}</span>
                              <span className="text-gray-400">•</span>
                              <span className="text-sm text-gray-700">{req.requester_name}</span>
                              {req.subsidiary && (
                                <>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">
                                    {SUBSIDIARIES.find(s => s.value === req.subsidiary)?.label || req.subsidiary}
                                  </span>
                                </>
                              )}
                              {req.SubsidiaryInfo && (
                                <>
                                  <span className="text-gray-400">•</span>
                                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">
                                    {req.SubsidiaryInfo.name}
                                  </span>
                                </>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-1">{req.item_service_requested}</p>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-teal-600">{formatCurrency(req.amount)}</p>
                          <p className="text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowFinanceApprovedRequestsModal(false)}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 rounded-lg hover:from-teal-700 hover:to-teal-800 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AP/AR Subsidiary Breakdown Modal */}
      <SubsidiaryBreakdownModal 
        isOpen={showAPARBreakdownModal}
        onClose={() => setShowAPARBreakdownModal(false)}
      />

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes expandWidth {
          from {
            width: 0;
          }
        }
      `}</style>
      </div>
      {/* Expenses Modal */}
      {showExpensesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <TrendingDown className="h-6 w-6" />
                  Total Expenses Breakdown
                </h3>
                <p className="text-red-100 mt-1">
                  {approvalHistory.length + financeApprovedHistory.length + pendingTransactions.length + approvedTransactions.length} items • Total: {formatCurrency(
                    [...approvalHistory, ...financeApprovedHistory].reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                    [...pendingTransactions, ...approvedTransactions].reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
                  )}
                </p>
              </div>
              <button
                onClick={() => setShowExpensesModal(false)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {/* Group all expenses by subsidiary */}
              {(() => {
                // Combine all expenses
                const allExpenses = [
                  ...(approvalHistory || []).map(req => ({ ...req, type: 'purchase_request' })),
                  ...(financeApprovedHistory || []).map(req => ({ ...req, type: 'purchase_request' })),
                  ...(pendingTransactions || []).map(txn => ({ ...txn, type: 'transaction' })),
                  ...(approvedTransactions || []).map(txn => ({ ...txn, type: 'transaction' }))
                ];

                // Group by subsidiary
                const groupedBySubsidiary = allExpenses.reduce((acc, item) => {
                  // Try to get subsidiary name from relationship first
                  let subsidiaryName = item.SubsidiaryInfo?.name;
                  
                  // If not available, try to map the subsidiary code to name
                  if (!subsidiaryName && item.subsidiary) {
                    const subsidiaryMatch = SUBSIDIARIES.find(s => s.value === item.subsidiary);
                    subsidiaryName = subsidiaryMatch ? subsidiaryMatch.label : item.subsidiary;
                  }
                  
                  // Default to 'Unassigned' if still no subsidiary
                  const subsidiary = subsidiaryName || 'Unassigned';
                  
                  if (!acc[subsidiary]) {
                    acc[subsidiary] = [];
                  }
                  acc[subsidiary].push(item);
                  return acc;
                }, {});

                // Sort subsidiaries alphabetically
                const sortedSubsidiaries = Object.keys(groupedBySubsidiary).sort();

                if (sortedSubsidiaries.length === 0) {
                  return (
                    <div className="text-center py-16 text-gray-500">
                      <TrendingDown className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-semibold">No expenses found</p>
                      <p className="text-sm mt-2">Expenses will appear here once recorded</p>
                    </div>
                  );
                }

                // Initialize selected subsidiary if not set
                if (!selectedSubsidiary && sortedSubsidiaries.length > 0) {
                  setSelectedSubsidiary(sortedSubsidiaries[0]);
                }

                const currentSubsidiary = selectedSubsidiary || sortedSubsidiaries[0];
                const selectedItems = groupedBySubsidiary[currentSubsidiary] || [];
                const totalAmount = selectedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
                const purchaseRequests = selectedItems.filter(item => item.type === 'purchase_request');
                const transactions = selectedItems.filter(item => item.type === 'transaction');

                return (
                  <>
                    {/* Subsidiary Tabs */}
                    <div className="mb-6 border-b border-gray-200">
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {sortedSubsidiaries.map((subsidiary) => {
                          const items = groupedBySubsidiary[subsidiary];
                          const total = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
                          const isSelected = currentSubsidiary === subsidiary;
                          
                          return (
                            <button
                              key={subsidiary}
                              onClick={() => setSelectedSubsidiary(subsidiary)}
                              className={`flex-shrink-0 px-6 py-3 rounded-t-lg font-semibold transition-all ${
                                isSelected
                                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <div className="text-left">
                                <div className="flex items-center gap-2">
                                  <Building className="h-4 w-4" />
                                  <span className="font-bold">{subsidiary}</span>
                                </div>
                                <div className="text-xs mt-1 opacity-90">
                                  {items.length} items • {formatCurrency(total)}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Selected Subsidiary Content */}
                    <div>
                      {/* Subsidiary Header */}
                      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded p-5 mb-6 shadow-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/10 rounded flex items-center justify-center">
                              <Building className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <h4 className="text-xl font-bold text-white">{currentSubsidiary}</h4>
                              <p className="text-gray-300 text-sm">{selectedItems.length} items</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Subsidiary Total</p>
                            <p className="text-3xl font-bold text-white">{formatCurrency(totalAmount)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Purchase Requests for selected subsidiary */}
                      {purchaseRequests.length > 0 && (
                        <div className="mb-6">
                          <h5 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2 ml-2">
                            <FileText className="h-4 w-4 text-purple-600" />
                            Purchase Requests ({purchaseRequests.length})
                          </h5>
                          <div className="space-y-3">
                            {purchaseRequests.map((req) => (
                              <div 
                                key={req.id} 
                                onClick={() => {
                                  setSelectedRequest(req);
                                  setShowDetailsModal(true);
                                  setShowExpensesModal(false);
                                }}
                                className="bg-gradient-to-br from-white to-purple-50 rounded border-2 border-purple-200 p-4 hover:shadow-lg hover:border-purple-400 transition-all cursor-pointer group"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                      <span className="font-mono text-sm font-semibold text-purple-900">
                                        {req.request_id}
                                      </span>
                                      <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded group-hover:bg-purple-200 transition-colors">
                                        Click to view details →
                                      </span>
                                    </div>
                                    
                                    {/* Status and Stage */}
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1">Status</p>
                                        <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                                          req.status === 'completed' ? 'bg-green-100 text-green-800' :
                                          req.status === 'payment_in_progress' ? 'bg-blue-100 text-blue-800' :
                                          req.status === 'awaiting_payment_confirmation' ? 'bg-yellow-100 text-yellow-800' :
                                          req.status === 'awaiting_delivery_confirmation' ? 'bg-teal-100 text-teal-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {req.status?.replace(/_/g, ' ')}
                                        </span>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1">Current Stage</p>
                                        <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                                          req.current_stage === 'completed' ? 'bg-green-100 text-green-800' :
                                          req.current_stage === 'delivery_stage' ? 'bg-teal-100 text-teal-800' :
                                          req.current_stage === 'pay_vendor_stage' ? 'bg-indigo-100 text-indigo-800' :
                                          req.current_stage === 'confirm_payment_stage' ? 'bg-yellow-100 text-yellow-800' :
                                          req.current_stage === 'finance_stage' ? 'bg-orange-100 text-orange-800' :
                                          'bg-purple-100 text-purple-800'
                                        }`}>
                                          {req.current_stage?.replace(/_stage|_/g, ' ').trim()}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-4 text-xs text-gray-600">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {new Date(req.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-2xl font-bold text-purple-900">
                                      {formatCurrency(req.amount)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Transactions for this subsidiary */}
                      {transactions.length > 0 && (
                        <div>
                          <h5 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2 ml-2">
                            <Banknote className="h-4 w-4 text-blue-600" />
                            Transactions ({transactions.length})
                          </h5>
                          <div className="space-y-3">
                            {transactions.map((txn) => (
                              <div 
                                key={txn.id} 
                                onClick={() => navigate('/finance/transactions')}
                                className="bg-gradient-to-br from-white to-blue-50 rounded border-2 border-blue-200 p-4 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer group"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                      <span className="font-mono text-sm font-semibold text-blue-900">
                                        {txn.transaction_number}
                                      </span>
                                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded group-hover:bg-blue-200 transition-colors">
                                        Click to view details →
                                      </span>
                                    </div>
                                    
                                    {/* Status and Type */}
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1">Status</p>
                                        <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                                          txn.status === 'approved' ? 'bg-green-100 text-green-800' :
                                          txn.status === 'pending_gmd_approval' ? 'bg-yellow-100 text-yellow-800' :
                                          txn.status === 'posted' ? 'bg-blue-100 text-blue-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {txn.status?.replace(/_/g, ' ')}
                                        </span>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1">Type</p>
                                        <span className="inline-flex items-center text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-800 capitalize">
                                          {txn.type}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-4 text-xs text-gray-600">
                                      <span className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {new Date(txn.date).toLocaleDateString()}
                                      </span>
                                      {txn.payment_method && (
                                        <span className="capitalize">{txn.payment_method}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-2xl font-bold text-blue-900">
                                      {formatCurrency(txn.amount)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="bg-gray-100 p-4 border-t flex items-center justify-end gap-3">
              <button
                onClick={() => setShowExpensesModal(false)}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employees on Leave Modal */}
      <EmployeesOnLeaveModal
        open={showOnLeaveModal}
        onClose={() => setShowOnLeaveModal(false)}
        leaves={onLeaveData.leaves}
      />
    </div>
  );
};

export default GMDDashboard;
